import { useEffect, useState } from "react"
import { Link } from "react-router"
import { useQuery } from "@tanstack/react-query"
import api from "@/lib/api"
import ModuleLayout from "@/components/admin/ModuleLayout"
import Badge from "@/components/ui/badge/Badge"
import { getErrorMessage } from "@/lib/errors"
import { LoadState } from "@/components/ui/LoadState"

type SeedbedSummary = {
  id: string
  name: string
  status: "active" | "paused" | "archived"
  institutionId: string
  institutionName: string
  hasRecentActivity: boolean
}

type InstitutionsSummary = {
  totalInstitutions: number
  totalSeedbeds: number
  byStatus: Record<"active" | "paused" | "archived", number>
  seedbeds: SeedbedSummary[]
}

type MembersSummary = {
  totalMembers: number
  byInstitution: {
    institutionId: string
    institutionName: string
    counts: Record<"active" | "suspended" | "ended", number>
    total: number
  }[]
}

type PaymentsSummary = {
  academy: Record<"pending" | "approved" | "declined" | "voided" | "error", number>
  cccd: Record<"pending" | "approved" | "declined" | "voided", number>
}

type InstitutionOption = { id: string; name: string }

const STATUS_LABELS: Record<string, string> = { active: "Activo", paused: "Pausado", archived: "Archivado" }
const ENROLLMENT_LABELS: Record<string, string> = { active: "Activos", suspended: "Suspendidos", ended: "Finalizados" }
const PAYMENT_LABELS: Record<string, string> = { pending: "Pendientes", approved: "Aprobados", declined: "Rechazados", voided: "Anulados", error: "Con error" }

function buildParams(institutionId: string, dateFrom: string, dateTo: string) {
  const params: Record<string, string> = {}
  if (institutionId) params.institutionId = institutionId
  if (dateFrom) params.dateFrom = dateFrom
  if (dateTo) params.dateTo = dateTo
  return params
}

export default function AdminVisibilityDashboardPage() {
  const [institutionId, setInstitutionId] = useState("")
  const [dateFrom, setDateFrom] = useState("")
  const [dateTo, setDateTo] = useState("")
  const [institutions, setInstitutions] = useState<InstitutionOption[]>([])

  useEffect(() => {
    api
      .get("/academy/seedbeds/admin/institutions", { params: { limit: 100 } })
      .then((res) => setInstitutions(res.data.data?.institutions ?? []))
      .catch(() => setInstitutions([]))
  }, [])

  const params = buildParams(institutionId, dateFrom, dateTo)

  const institutionsQuery = useQuery({
    queryKey: ["admin-visibility", "institutions-summary", params],
    queryFn: async () => (await api.get<{ data: InstitutionsSummary }>("/admin/visibility/institutions-summary", { params })).data.data,
  })
  const membersQuery = useQuery({
    queryKey: ["admin-visibility", "members-summary", params],
    queryFn: async () => (await api.get<{ data: MembersSummary }>("/admin/visibility/members-summary", { params })).data.data,
  })
  const paymentsQuery = useQuery({
    queryKey: ["admin-visibility", "payments-summary", { dateFrom, dateTo }],
    queryFn: async () => (await api.get<{ data: PaymentsSummary }>("/admin/visibility/payments-summary", { params: { dateFrom, dateTo } })).data.data,
  })

  return (
    <ModuleLayout
      breadcrumbs={[{ label: "SateLab", to: "/" }, "Sistema", "Panel de visibilidad"]}
      title="Panel de visibilidad"
      subtitle="Instituciones, semilleros, miembros y pagos, consolidados en un solo lugar"
      tabs={[]}
      metrics={[]}
    >
      <div className="mb-6 flex flex-wrap items-end gap-4 rounded-xl border border-gray-200 p-4 dark:border-gray-800">
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Institución</label>
          <select
            className="h-10 rounded-lg border border-gray-300 bg-white px-3 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white"
            value={institutionId}
            onChange={(e) => setInstitutionId(e.target.value)}
          >
            <option value="">Todas</option>
            {institutions.map((inst) => (
              <option key={inst.id} value={inst.id}>{inst.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Desde</label>
          <input
            type="date"
            className="h-10 rounded-lg border border-gray-300 bg-white px-3 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Hasta</label>
          <input
            type="date"
            className="h-10 rounded-lg border border-gray-300 bg-white px-3 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </div>
      </div>

      <div className="space-y-6">
        {/* Instituciones y semilleros */}
        <section className="rounded-xl border border-gray-200 p-5 dark:border-gray-800">
          <h3 className="mb-3 font-semibold text-gray-900 dark:text-white">Instituciones y semilleros</h3>
          {institutionsQuery.isLoading && <LoadState />}
          {institutionsQuery.isError && <p className="text-sm text-error-600 dark:text-error-400">{getErrorMessage(institutionsQuery.error)}</p>}
          {institutionsQuery.data && (
            <>
              <div className="mb-4 flex flex-wrap gap-6 text-sm">
                <span>Instituciones: <strong>{institutionsQuery.data.totalInstitutions}</strong></span>
                <span>Semilleros: <strong>{institutionsQuery.data.totalSeedbeds}</strong></span>
                {(["active", "paused", "archived"] as const).map((s) => (
                  <span key={s}>{STATUS_LABELS[s]}: <strong>{institutionsQuery.data!.byStatus[s]}</strong></span>
                ))}
              </div>
              <ul className="divide-y divide-gray-100 dark:divide-gray-800">
                {institutionsQuery.data.seedbeds.map((s) => (
                  <li key={s.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                    <div>
                      <Link to={`/programs/seedbeds/${s.id}`} className="font-medium text-gray-900 hover:underline dark:text-white">{s.name}</Link>
                      <span className="ml-2 text-gray-500">{s.institutionName}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {!s.hasRecentActivity && <Badge color="warning" size="sm">Sin actividad reciente</Badge>}
                      <Badge color={s.status === "active" ? "success" : s.status === "paused" ? "warning" : "light"} size="sm">
                        {STATUS_LABELS[s.status]}
                      </Badge>
                    </div>
                  </li>
                ))}
                {institutionsQuery.data.seedbeds.length === 0 && (
                  <li className="py-2 text-sm text-gray-500">Sin semilleros para este filtro.</li>
                )}
              </ul>
            </>
          )}
        </section>

        {/* Miembros */}
        <section className="rounded-xl border border-gray-200 p-5 dark:border-gray-800">
          <h3 className="mb-3 font-semibold text-gray-900 dark:text-white">Miembros por institución</h3>
          {membersQuery.isLoading && <LoadState />}
          {membersQuery.isError && <p className="text-sm text-error-600 dark:text-error-400">{getErrorMessage(membersQuery.error)}</p>}
          {membersQuery.data && (
            <ul className="divide-y divide-gray-100 dark:divide-gray-800">
              {membersQuery.data.byInstitution.map((entry) => (
                <li key={entry.institutionId} className="flex items-center justify-between gap-3 py-2 text-sm">
                  <span className="font-medium text-gray-900 dark:text-white">{entry.institutionName}</span>
                  <div className="flex items-center gap-2">
                    {(["active", "suspended", "ended"] as const).map((status) => (
                      <span key={status} className="text-gray-500">
                        {ENROLLMENT_LABELS[status]}: <strong className="text-gray-900 dark:text-white">{entry.counts[status] ?? 0}</strong>
                      </span>
                    ))}
                  </div>
                </li>
              ))}
              {membersQuery.data.byInstitution.length === 0 && (
                <li className="py-2 text-sm text-gray-500">Sin miembros para este filtro.</li>
              )}
            </ul>
          )}
        </section>

        {/* Pagos */}
        <section className="rounded-xl border border-gray-200 p-5 dark:border-gray-800">
          <h3 className="mb-3 font-semibold text-gray-900 dark:text-white">Pagos (Academy vs. CCCD)</h3>
          {paymentsQuery.isLoading && <LoadState />}
          {paymentsQuery.isError && <p className="text-sm text-error-600 dark:text-error-400">{getErrorMessage(paymentsQuery.error)}</p>}
          {paymentsQuery.data && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <p className="mb-1 text-xs font-semibold uppercase text-gray-500">Academy</p>
                <div className="flex flex-wrap gap-3 text-sm">
                  {(Object.keys(PAYMENT_LABELS) as (keyof PaymentsSummary["academy"])[]).map((s) => (
                    <span key={s}>{PAYMENT_LABELS[s]}: <strong>{paymentsQuery.data!.academy[s] ?? 0}</strong></span>
                  ))}
                </div>
              </div>
              <div>
                <p className="mb-1 text-xs font-semibold uppercase text-gray-500">CCCD</p>
                <div className="flex flex-wrap gap-3 text-sm">
                  {(["pending", "approved", "declined", "voided"] as const).map((s) => (
                    <span key={s}>{PAYMENT_LABELS[s]}: <strong>{paymentsQuery.data!.cccd[s] ?? 0}</strong></span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
    </ModuleLayout>
  )
}
